import React, { useState } from "react";
import styled from "styled-components";

const Section = styled.div`
  border-bottom: 1px solid var(--bg-200);

  &:first-of-type {
    border-top: 1px solid var(--bg-200);
  }
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--spacing-md) 0;
  background: none;
  border: none;
  color: var(--text-100);
  text-align: left;
  gap: var(--spacing-sm);
  transition: background-color 0.15s ease-in-out;

  &:hover,
  &:focus-visible {
    background-color: var(--bg-200);
  }
`;

const SectionTitle = styled.span`
  flex: 1;
  font-size: var(--font-size-base);
  font-weight: 500;
`;

const CountBadge = styled.span`
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--border-radius-large);
  background-color: var(--primary-300);
  color: var(--primary-100);
  font-size: var(--font-size-small);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Chevron = styled.svg`
  flex-shrink: 0;
  transition: transform 0.25s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0)")};
`;

const Panel = styled.div`
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? "1000px" : "0px")};
  transition: max-height 0.3s ease;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 4px 10px var(--spacing-md);
  cursor: pointer;
  border-radius: var(--border-radius-small);

  &:hover {
    background-color: var(--bg-200);
  }
`;

const Checkbox = styled.div`
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid ${({ $checked }) =>
    $checked ? "var(--primary-100)" : "var(--bg-300)"};
  background-color: ${({ $checked }) =>
    $checked ? "var(--primary-100)" : "transparent"};
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const OptionLabel = styled.label`
  flex: 1;
  font-size: var(--font-size-base);
  color: var(--text-100);
  cursor: pointer;
`;

const OptionCount = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const CheckIcon = () => (
  <svg
    fill="var(--bg-100)"
    width="12px"
    height="12px"
    viewBox="0 0 1024 1024"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M760 380.4l-61.6-61.6-263.2 263.1-109.6-109.5L264 534l171.2 171.2L760 380.4z" />
  </svg>
);

/**
 * Jedna sekcija filtera (Brand / Format / Strength...) u akordeon prikazu -
 * zamenjuje stari "drill-down" (tap -> pun-ekran podlista -> Back) jednim
 * skrolabilnim panelom gde se sekcije samo otvaraju/zatvaraju na mestu.
 */
const FilterSection = ({
  title,
  options,
  selectedValues,
  onToggle,
  getCount,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionHeader
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <SectionTitle>{title}</SectionTitle>
        {selectedValues.size > 0 && (
          <CountBadge>{selectedValues.size}</CountBadge>
        )}
        <Chevron
          $isOpen={isOpen}
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="var(--text-200)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Chevron>
      </SectionHeader>
      <Panel $isOpen={isOpen}>
        {options.map((option) => {
          const checked = selectedValues.has(option);
          return (
            <OptionRow
              key={option}
              onClick={() => onToggle(option)}
              role="checkbox"
              aria-checked={checked}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle(option);
                }
              }}
            >
              <Checkbox $checked={checked}>
                {checked && <CheckIcon />}
              </Checkbox>
              <OptionLabel>{option}</OptionLabel>
              <OptionCount>{getCount(option)}</OptionCount>
            </OptionRow>
          );
        })}
      </Panel>
    </Section>
  );
};

export default FilterSection;
